import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import CreateWishlistModal from '../pages/wishlists/components/CreateWishlistModal';
import { wishlistsService } from '../services/wishlists';

const WishlistSelectionModal = ({
    isOpen,
    onClose,
    product,
    onAddToWishlist,
    userWishlists = []
}) => {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSelectWishlist = async (wishlist) => {
        setLoading(true);
        setError(null);

        try {
            await wishlistsService.addProductToWishlist(wishlist.id, product.id);
            onAddToWishlist?.(wishlist);
            onClose();
        } catch (error) {
            console.error('Error adding to wishlist:', error);
            setError('Failed to add to wishlist. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateWishlist = async (wishlistData) => {
        setLoading(true);
        setError(null);

        try {
            // Create the wishlist
            const newWishlist = await wishlistsService.createWishlist(wishlistData);

            // Add the product to the newly created wishlist
            await wishlistsService.addProductToWishlist(newWishlist.id, product.id);

            onAddToWishlist?.(newWishlist);
            setShowCreateModal(false);
            onClose();
        } catch (error) {
            console.error('Error creating wishlist and adding product:', error);
            setError('Failed to create wishlist. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Main Modal */}
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
                    {/* Header */}
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-heading font-semibold text-gray-800">
                                Save to Wishlist
                            </h2>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <Icon name="X" size={20} />
                            </button>
                        </div>
                        {product && (
                            <div className="mt-3 flex items-center text-sm text-gray-600">
                                <div className="w-12 h-12 bg-gray-100 rounded-lg mr-3 overflow-hidden">
                                    {product.images?.[0]?.image && (
                                        <img
                                            src={product.images[0].image}
                                            alt={product.name}
                                            className="w-full h-full object-cover"
                                        />
                                    )}
                                </div>
                                <div>
                                    <p className="font-medium text-gray-800 line-clamp-1">{product.name}</p>
                                    <p className="text-sm text-gray-500">${product.retail_price}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                                {error}
                            </div>
                        )}

                        {/* Create New Wishlist Button */}
                        <button
                            onClick={() => setShowCreateModal(true)}
                            disabled={loading}
                            className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-primary hover:bg-primary-50 transition-colors mb-4 disabled:opacity-50"
                        >
                            <div className="flex items-center justify-center text-primary">
                                <Icon name="Plus" size={20} className="mr-2" />
                                <span className="font-medium">Create New Wishlist</span>
                            </div>
                        </button>

                        {/* Existing Wishlists */}
                        <div className="space-y-2">
                            {userWishlists.length > 0 ? (
                                <>
                                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                                        Choose from your wishlists:
                                    </h3>
                                    {userWishlists.map((wishlist) => (
                                        <button
                                            key={wishlist.id}
                                            onClick={() => handleSelectWishlist(wishlist)}
                                            disabled={loading}
                                            className="w-full p-3 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary-50 transition-colors text-left disabled:opacity-50"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="font-medium text-gray-800">{wishlist.name}</p>
                                                    {wishlist.description && (
                                                        <p className="text-sm text-gray-500 line-clamp-1">
                                                            {wishlist.description}
                                                        </p>
                                                    )}
                                                    <p className="text-xs text-gray-400 mt-1">
                                                        {wishlist.items?.length || 0} items
                                                    </p>
                                                </div>
                                                <Icon name="ChevronRight" size={16} className="text-gray-400" />
                                            </div>
                                        </button>
                                    ))}
                                </>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Icon name="Heart" size={24} className="text-gray-400" />
                                    </div>
                                    <p className="text-gray-600 mb-2">No wishlists yet</p>
                                    <p className="text-sm text-gray-500">
                                        Create your first wishlist to start saving your favorite gifts
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t border-gray-200 bg-gray-50">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            className="w-full"
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </div>

            {/* Create Wishlist Modal */}
            <CreateWishlistModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreateWishlist={handleCreateWishlist}
            />
        </>
    );
};

export default WishlistSelectionModal; 